import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import DataConfigParameterDisplay from 'components/WellList/DataConfigModal/DataConfigParameterDisplay'
import { getDetails, getAlertTypePatch } from './AddAlertDialogDefs'
import { Icon as Iconify } from '@iconify/react'
import { unescapeHtml } from 'utils/htmlSymbolHandling'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { appColors } from 'utils'

function toNumberOrNull(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function uniq(arr) {
  const seen = new Set()
  const out = []
  for (const v of arr || []) {
    const s = String(v ?? '').trim()
    if (!s) continue
    if (seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out
}

function buildAlertFromRow(row, defaultAlerts) {
  const base = defaultAlerts?.find((a) => a.name === row.name)
  const phaseArr = uniq(row.phase)
  const rigArr = uniq(row.rig)

  const baseUseMin = Boolean(base?.useMin)
  const baseUseMax = Boolean(base?.useMax)

  return {
    name: row.name,
    severity: row.severity,
    phase: phaseArr.length > 0 ? phaseArr.join('|') : 'All',
    rig: rigArr.length > 0 ? rigArr.join('|') : 'All',
    ignoreProjections: row.ignoreProjections,
    silenceAlert: row.silenceAlert,
    useEmail: row.useEmail,
    useSms: row.useSms,
    useDashboards: row.useDashboards,

    unit: base?.unit,
    uid: base?.uid,
    active: true,

    useMin: baseUseMin,
    useMax: baseUseMax,

    min: baseUseMin ? (toNumberOrNull(row.min) ?? base?.min) : base?.min,
    max: baseUseMax ? (toNumberOrNull(row.max) ?? base?.max) : base?.max,

    useMinDepth: Boolean(row.useMinDepth),
    minDepth: row.useMinDepth ? toNumberOrNull(row.minDepth) : null,
    useMaxDepth: Boolean(row.useMaxDepth),
    maxDepth: row.useMaxDepth ? toNumberOrNull(row.maxDepth) : null,
  }
}

const emptyRow = {
  name: '',
  severity: '',
  phase: [],
  rig: [],
  min: '',
  max: '',
  useMinDepth: false,
  minDepth: '',
  useMaxDepth: false,
  maxDepth: '',
  ignoreProjections: false,
  silenceAlert: false,
  useEmail: false,
  useSms: false,
  useDashboards: false,
}

const AddAlertDialog = ({
  open,
  onClose,
  onSubmit,
  alertTypeDropDowns,
  severityDropDowns,
  phaseDropDowns,
  rigDropDowns,
  defaultAlerts,
  showProjections,
  alertCategory,
  alertToEdit = null,
  smsAvailable = false,
}) => {
  const { theme, getTextColor, getDisabledColor, getCardBackground, getBackColor, getWindowBarColor } = useInnovaTheme()

  const inputSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiInputBase-root': {
        minHeight: 40,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
    }),
    [getBackColor, getTextColor, getDisabledColor, theme],
  )

  const shouldShowDepths = useMemo(() => {
    if (alertCategory === 'Anti-Collision' || alertCategory === 'Drilling Parameters') return true
    return false
  }, [alertCategory])

  const textColor = getTextColor()
  const cardBg = getCardBackground()

  const [rows, setRows] = useState([{ ...emptyRow }])

  function alertToRow(alert) {
    if (!alert) return null

    return {
      name: alert.name || '',
      severity: alert.severity || '',
      phase: alert.phase && alert.phase !== 'All' ? String(alert.phase).split('|').filter(Boolean) : [],
      rig: alert.rig && alert.rig !== 'All' ? String(alert.rig).split('|').filter(Boolean) : [],
      min: alert.min ?? '',
      max: alert.max ?? '',
      useMinDepth: Boolean(alert.useMinDepth),
      minDepth: alert.minDepth ?? '',
      useMaxDepth: Boolean(alert.useMaxDepth),
      maxDepth: alert.maxDepth ?? '',
      ignoreProjections: Boolean(alert.ignoreProjections),
      silenceAlert: Boolean(alert.silenceAlert),
      useEmail: Boolean(alert.useEmail),
      useSms: Boolean(alert.useSms),
      useDashboards: Boolean(alert.useDashboards),
    }
  }

  const defaultSeverity =
    Array.isArray(severityDropDowns) && severityDropDowns.length === 1 ? unescapeHtml(severityDropDowns[0]) : ''

  useEffect(() => {
    if (!open) return

    if (alertToEdit) {
      setRows([alertToRow(alertToEdit)])
      return
    }

    setRows([
      {
        ...emptyRow,
        severity: defaultSeverity,
      },
    ])
  }, [open, defaultSeverity, alertToEdit])

  const canSubmit = useMemo(() => {
    return rows.every((r) => r.name && r.severity)
  }, [rows])

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], ...patch }
      return next
    })
  }

  const handleSubmit = () => {
    const created = rows.map((r) => buildAlertFromRow(r, defaultAlerts))

    if (alertToEdit) {
      onSubmit?.({
        ...alertToEdit,
        ...created[0],
      })
      return
    }

    onSubmit?.(created)
  }

  const autoCompleteSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiOutlinedInput-root': {
        minHeight: 40,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
      '& .MuiChip-root': {
        backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        color: getTextColor(),
      },
      '& .MuiChip-deleteIcon': {
        color: getDisabledColor(),
      },
    }),
    [getBackColor, getTextColor, getDisabledColor, theme],
  )

  const autoCompleteSlotProps = useMemo(
    () => ({
      paper: {
        sx: {
          backgroundColor: cardBg,
          color: textColor,
          border: theme === 'dark' ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.10)',
        },
      },
      popper: {
        sx: {
          '& .MuiAutocomplete-listbox': {
            backgroundColor: cardBg,
            color: textColor,
            padding: 0,
          },
          '& .MuiAutocomplete-option': {
            color: textColor,
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
          },
          '& .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          },
        },
      },
    }),
    [cardBg, textColor, theme],
  )

  const showErrors = useMemo(() => {
    return rows.map((r) => ({
      name: !r.name,
      severity: !r.severity,
    }))
  }, [rows])

  const renderSelect = (r, idx, field, options) => (
    <TextField
      select
      fullWidth
      size='small'
      value={r[field]}
      error={showErrors[idx]?.[field]}
      helperText={showErrors[idx]?.[field] ? 'Required' : ''}
      onChange={(event) => {
        if (field === 'name') {
          updateRow(idx, getAlertTypePatch(event.target.value, defaultAlerts))
          return
        }

        updateRow(idx, {
          [field]: unescapeHtml(event.target.value ?? ''),
        })
      }}
      sx={{
        ...inputSx,
        minWidth: field === 'name' ? 200 : undefined,
      }}>
      {(options || []).map((value) => (
        <MenuItem key={value} value={unescapeHtml(value)}>
          {unescapeHtml(value)}
        </MenuItem>
      ))}
    </TextField>
  )

  const renderMultiSelect = (r, idx, field, options) => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size='small'
      options={options || []}
      value={r[field]}
      onChange={(_, next) => {
        updateRow(idx, { [field]: uniq(next) })
      }}
      sx={{
        ...autoCompleteSx,
        minWidth: 200,
      }}
      slotProps={autoCompleteSlotProps}
      renderInput={(params) => <TextField {...params} />}
    />
  )

  const renderDepthLimit = (r, idx, field, enabledField) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
      }}>
      <Checkbox
        size='small'
        checked={r[enabledField]}
        onChange={(event) => {
          updateRow(idx, {
            [enabledField]: event.target.checked,
          })
        }}
        sx={{
          p: 0.25,
          color: textColor,
        }}
      />

      <TextField
        fullWidth
        size='small'
        type='number'
        value={r[field]}
        disabled={!r[enabledField]}
        onChange={(event) => {
          updateRow(idx, {
            [field]: event.target.value,
          })
        }}
        sx={inputSx}
      />
    </Box>
  )

  const renderSms = (r, idx) => {
    const checkbox = (
      <Checkbox
        size='small'
        checked={r.useSms}
        disabled={!smsAvailable}
        onChange={(event) => {
          if (!smsAvailable) return

          updateRow(idx, {
            useSms: event.target.checked,
          })
        }}
        sx={{
          p: 0.25,
          color: textColor,
        }}
      />
    )

    if (smsAvailable) return checkbox

    return (
      <Tooltip
        arrow
        placement='bottom'
        enterDelay={300}
        title={
          <Box sx={{ p: 0.75, maxWidth: 260 }}>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 500,
                color: textColor,
                mb: 0.75,
              }}>
              SMS is unavailable, it can be enabled in the User settings.
            </Typography>

            <Box
              sx={{
                borderTop: 1,
                borderColor: getDisabledColor(),
                pt: 0.75,
              }}>
              <Typography
                variant='caption'
                sx={{
                  fontStyle: 'italic',
                  color: getDisabledColor(),
                }}>
                SMS is only available in the US, CA, or UK.
              </Typography>
            </Box>
          </Box>
        }
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: cardBg,
              color: textColor,
              border: `1px solid ${getDisabledColor()}`,
              borderRadius: 1.5,
              maxWidth: 300,
            },
          },
          arrow: {
            sx: {
              color: cardBg,
              '&::before': {
                border: `1px solid ${getDisabledColor()}`,
              },
            },
          },
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
            ],
          },
        }}>
        <span style={{ display: 'inline-flex' }}>{checkbox}</span>
      </Tooltip>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          backgroundColor: getWindowBarColor(),
          width: '800px',
          maxWidth: '95vw',
          maxHeight: '90vh',
        },
      }}>
      <Box
        sx={{
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          color: appColors.headerTextColor,
          backgroundColor: getWindowBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 600,
            flex: 1,
          }}>
          <Iconify icon={alertToEdit ? 'mdi:pencil-outline' : 'fluent:add-12-filled'} />
          {alertToEdit ? 'Edit Alert' : 'Add Alert'}
        </Typography>

        <IconButton onClick={onClose} size='small'>
          <Iconify icon='mdi:close' />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 1.5,
          backgroundColor: getBackColor(),
        }}>
        {rows.map((r, idx) => {
          const base = defaultAlerts?.find((alert) => alert.name === r.name)

          const detailsDefs = getDetails(r, base, shouldShowDepths, showProjections, {
            useSms: () => renderSms(r, idx),
            name: () => renderSelect(r, idx, 'name', alertTypeDropDowns),
            severity: () => renderSelect(r, idx, 'severity', severityDropDowns),
            phase: () => renderMultiSelect(r, idx, 'phase', phaseDropDowns),
            rig: () => renderMultiSelect(r, idx, 'rig', rigDropDowns),
            minDepth: () => renderDepthLimit(r, idx, 'minDepth', 'useMinDepth'),
            maxDepth: () => renderDepthLimit(r, idx, 'maxDepth', 'useMaxDepth'),
          })

          const handleUpdate = ({ tag, value }) => {
            updateRow(idx, {
              [tag]: tag === 'ignoreProjections' ? !value : value,
            })
          }

          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                width: '100%',
                minWidth: 0,
                mb: 1.5,
              }}>
              <DataConfigParameterDisplay
                title='Alert Settings'
                defs={detailsDefs.settings}
                onChange={handleUpdate}
                display='flex'
                labelWidth={90}
              />

              <DataConfigParameterDisplay
                title='Alert Details'
                defs={detailsDefs.alertDetails}
                onChange={handleUpdate}
                labelWidth={115}
                editorMaxWidth={690}
              />

              {detailsDefs.depthLimits.length > 0 && (
                <DataConfigParameterDisplay
                  title='Depth Limits'
                  defs={detailsDefs.depthLimits}
                  onChange={handleUpdate}
                  labelWidth={115}
                  editorMaxWidth={690}
                />
              )}

              <DataConfigParameterDisplay
                title='Options'
                defs={detailsDefs.options}
                onChange={handleUpdate}
                labelWidth={160}
                editorMaxWidth={690}
              />
            </Box>
          )
        })}
      </DialogContent>

      <DialogActions
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: getWindowBarColor(),
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Button variant='outlined' color='secondary' onClick={onClose}>
          Cancel
        </Button>

        <Button variant='outlined' color='primary' onClick={handleSubmit} disabled={!canSubmit}>
          {alertToEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddAlertDialog
